import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { fetchRmsOutputs } from "./rmsApi";
import {
  createRmsTranslator,
  formatRmsCount,
  getRmsLocale,
  translateRmsStatus,
} from "./rmsPmsI18n";
import { formatCurrency, formatDateLabel, formatPercent, statusTone } from "./rmsShared";
import RmsPipelineStatus from "./RmsPipelineStatus.jsx";
import { resolveRmsPipelineStatus } from "./rmsPipelineStatus";

const ROW_LIMIT = 400;

const HELP_TEXT = {
  forecastRun:
    "Latest successful demand forecast run feeding this screen. It calculates current occupancy, expected cancellations, pickup, final occupancy and forecast confidence.",
  dailyFeaturesRun:
    "Latest successful daily features run feeding this screen. It enriches forecast rows with prices, market baseline, stop sell status and the active strategy context.",
  rowsLoaded:
    "How many room category plus stay date rows matched the current filter. Returned rows can be capped by the screen limit.",
  avgCurrentOccupancy:
    "Average current on-books occupancy across the rows shown. Formula per row: current on-books rooms divided by inventory rooms.",
  avgFinalOccupancy:
    "Average forecast final occupancy across the rows shown. Formula per row: (on-books rooms - expected cancel rooms + expected net pickup rooms) divided by inventory rooms.",
  avgExpectedCancelPct:
    "Average share of current on-books rooms expected to cancel before arrival. Taken from RMS cancellation curves by lead time, with hotel fallback when category history is missing.",
  marketSignalRows:
    "How many rows have at least one comparable competitor price feeding the market baseline.",
  stopSellRows:
    "How many rows are blocked because there is no availability, no active room rates, or every active rate is stop-sold.",
  roomCategory:
    "Your PMS room category. This is the pricing unit the RMS works with.",
  stayDate: "The calendar day this forecast row applies to.",
  currentOccupancy:
    "Current on-books occupancy for this room category and stay date. Formula: current on-books rooms divided by inventory rooms.",
  finalOccupancy:
    "Forecast final occupancy for this room category and stay date. Formula: on-books rooms minus expected cancel rooms plus expected net pickup rooms, all divided by inventory rooms.",
  onBooks:
    "Rooms already on the books for this stay date. Source: forecast_occupancy snapshot used by the demand forecast run.",
  cancelPct:
    "Expected share of current on-books rooms likely to cancel before arrival. Source: RMS cancellation curves resolved by room category and lead time, with hotel fallback if needed.",
  cancelRooms:
    "Expected number of rooms likely to cancel before arrival. Formula: current on-books rooms multiplied by expected cancel percentage.",
  pickup:
    "Expected gross additional bookings between now and arrival. Source: historical pickup patterns for comparable stay dates and the same current lead time.",
  netPickup:
    "Expected net room gain between now and arrival after historical net movement. Source: net pickup history built from forecast occupancy snapshots.",
  marketBaseline:
    "Comparable competitor price baseline for this row. It uses the weighted average matched price from active competitors after mapping your room category and competitor room types to the same comparable market bucket.",
  marketSources:
    "How many comparable competitor hotels actually contributed a matched price to the market baseline for this stay date.",
  marketQuality:
    "Quality decision captured with the daily feature. Stale, mismatched, anomalous or insufficiently covered prices are ignored before they can influence a recommendation.",
  currentPrice:
    "Current PMS selling price loaded for this room category and stay date from active room rates.",
  basePrice:
    "Internal RMS starting price before any recommendation logic. Source order: current PMS price, else market baseline, else booked ADR, else floor price.",
  strategy:
    "Active RMS strategy mode applied to this row. A room-category rule wins first; if there is none, the hotel-wide rule is used.",
  forecastConfidence:
    "How reliable the forecast is for this row. The RMS scores cancellation-curve quality, pickup-history coverage, forecast horizon, current on-books anchor and whether seasonality or market disruption context was resolved.",
  paceWindow:
    "The configured occupancy pace window selected from the row's lead days. A blank value means this legacy row has no occupancy pace snapshot.",
  paceTarget:
    "Expected on-books occupancy at this lead time. Formula: the seasonality profile's expected final occupancy multiplied by the configured expected booked share for this window.",
  paceCurrent:
    "Commercial occupancy observed for the pace comparison, with the on-books room count and effective inventory captured in the same snapshot.",
  paceGap:
    "Difference between current commercial occupancy and the expected occupancy target. Positive means ahead of pace; negative means behind.",
  paceStatus:
    "Commercial pace classification after applying the configured tolerance: ahead, on pace or behind. Legacy rows without a snapshot remain unconfigured.",
};

const PACE_WINDOW_LABELS = {
  "0_2": "0–2 days",
  "3_7": "3–7 days",
  "8_21": "8–21 days",
  "22_60": "22–60 days",
  "61_plus": "61+ days",
};

const PACE_STATUS_LABELS = {
  ahead: "Ahead",
  on_pace: "On pace",
  behind: "Behind",
};

const STRATEGY_LABELS = {
  occupancy: "Fill rooms",
  adr: "Protect rate",
  balanced: "Balanced",
};

function cleanPlaceholder(value) {
  return value === "\u2014" || value === "\u00e2\u20ac\u201d" ? "-" : value;
}

function safeFormatCurrency(value, locale, currency) {
  return cleanPlaceholder(formatCurrency(value, locale, currency));
}

function safeFormatPercent(value, locale, decimals = 1) {
  return cleanPlaceholder(formatPercent(value, decimals, locale));
}

function firstPresent(...values) {
  return values.find(
    (value) => value != null && !(typeof value === "string" && !value.trim())
  );
}

function formatSignedPercent(value, locale, decimals = 1) {
  const numeric = Number(value);
  if (value == null || value === "" || !Number.isFinite(numeric)) return "-";
  const formatted = Math.abs(numeric * 100).toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  if (numeric === 0) return `${formatted}%`;
  return `${numeric > 0 ? "+" : "-"}${formatted}%`;
}

function getPaceWindowCode(row) {
  return firstPresent(
    row?.pace_window_code,
    row?.pace_lead_time_bucket,
    row?.lead_time_bucket
  );
}

function getPaceLeadDays(row) {
  return firstPresent(row?.pace_lead_days, row?.lead_days, row?.days_out);
}

function getPaceExpectedOccupancy(row) {
  return firstPresent(
    row?.pace_expected_occupancy_pct,
    row?.expected_on_books_occupancy_pct
  );
}

function getPaceCurrentOccupancy(row) {
  return firstPresent(row?.pace_current_occupancy_pct, row?.current_occupancy_pct);
}

function getPaceStatus(row) {
  const status = String(firstPresent(row?.pace_status, "") || "").trim();
  return Object.prototype.hasOwnProperty.call(PACE_STATUS_LABELS, status)
    ? status
    : "";
}

function paceStatusTone(status) {
  if (status === "ahead") return "success";
  if (status === "behind") return "danger";
  if (status === "on_pace") return "soft";
  return "warn";
}

function safeFormatDateLabel(value, locale) {
  return cleanPlaceholder(formatDateLabel(value, locale));
}

function formatConfidence(value, locale) {
  return safeFormatPercent(value, locale, 0);
}

function HelpDot({ text }) {
  return (
    <span className="rmo-helpDot" title={text} aria-label={text}>
      ?
    </span>
  );
}

function LabelWithHelp({ label, helpText, className = "" }) {
  return (
    <span className={`rmo-helpLabel ${className}`.trim()}>
      <span>{label}</span>
      <HelpDot text={helpText} />
    </span>
  );
}

function buildRunTitle(t, languageId, label, helpText, run) {
  const lines = [`${label}: ${helpText}`];
  if (run?.status) lines.push(`${t("Status")}: ${translateRmsStatus(languageId, run.status)}`);
  if (run?.date_from || run?.date_to) {
    lines.push(
      `${t("Date range")}: ${String(run?.date_from || "-").slice(0, 10)} -> ${String(
        run?.date_to || "-"
      ).slice(0, 10)}`
    );
  }
  if (run?.started_at) lines.push(`${t("Started")}: ${run.started_at}`);
  if (run?.finished_at) lines.push(`${t("Finished")}: ${run.finished_at}`);
  return lines.join("\n");
}

function RunPill({ t, languageId, label, run, helpText }) {
  const status = run?.status || "missing";
  return (
    <span
      className={`rmo-pill rmo-pill--${statusTone(status)}`}
      title={buildRunTitle(t, languageId, label, helpText, run)}
    >
      <LabelWithHelp label={label} helpText={helpText} />
      <span>{translateRmsStatus(languageId, status)}</span>
    </span>
  );
}

export default function RmsSignalsForecastView({ businessUnitId }) {
  const { languageId, language } = usePmsTheme();
  const t = useMemo(() => createRmsTranslator(languageId), [languageId]);
  const locale = getRmsLocale(languageId, language);
  const dateLocale = languageId === "en" ? "en-GB" : locale || "es-ES";

  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [roomCategoryId, setRoomCategoryId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const requestRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const loadMeta = async () => {
      try {
        const payload = await fetchRmsOutputs("meta", {
          business_unit_id: businessUnitId,
        });
        if (cancelled) return;
        setMeta(payload);
        setDateFrom((current) => current || payload?.defaults?.signals_date_from || "");
        setDateTo((current) => current || payload?.defaults?.signals_date_to || "");
      } catch (nextError) {
        if (!cancelled) {
          setError(nextError?.response?.data?.error || nextError?.message || t("Could not load RMS meta."));
        }
      }
    };
    loadMeta();
    return () => {
      cancelled = true;
    };
  }, [businessUnitId, t]);

  const loadSignals = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    if (!dateFrom || !dateTo) {
      setData(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError("");
      setData(null);
      const payload = await fetchRmsOutputs("signals-forecast", {
        business_unit_id: businessUnitId,
        room_category_id: roomCategoryId || undefined,
        date_from: dateFrom,
        date_to: dateTo,
        limit: ROW_LIMIT,
      });
      if (requestRef.current !== requestId) return;
      setData(payload);
    } catch (nextError) {
      if (requestRef.current !== requestId) return;
      setData(null);
      setError(nextError?.response?.data?.error || nextError?.message || t("Could not load RMS signals."));
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, [businessUnitId, dateFrom, dateTo, roomCategoryId, t]);

  useEffect(() => {
    void loadSignals();
    return () => {
      requestRef.current += 1;
    };
  }, [loadSignals]);

  const summaryCards = useMemo(() => {
    const summary = data?.summary || {};
    const rowsByPaceStatus = summary.rows_by_pace_status || {};
    return [
      {
        label: t("Rows loaded"),
        value: formatRmsCount(summary.rows_count, locale),
        meta: `${data?.returned_rows || 0}/${data?.total_rows || 0} ${t("Rows loaded").toLowerCase()}`,
        tooltip: t(HELP_TEXT.rowsLoaded),
      },
      {
        label: t("Avg current occupancy"),
        value: safeFormatPercent(summary.avg_current_occupancy_pct, locale),
        meta: t("Current on-books category occupancy"),
        tooltip: t(HELP_TEXT.avgCurrentOccupancy),
      },
      {
        label: t("Avg final occupancy"),
        value: safeFormatPercent(summary.avg_expected_final_occupancy_pct, locale),
        meta: t("Main forward demand signal"),
        tooltip: t(HELP_TEXT.avgFinalOccupancy),
      },
      {
        label: t("Avg expected cancel pct"),
        value: safeFormatPercent(summary.avg_expected_cancel_pct, locale),
        meta: t("Cancellation pressure before arrival"),
        tooltip: t(HELP_TEXT.avgExpectedCancelPct),
      },
      {
        label: t("Market signal rows"),
        value: formatRmsCount(summary.rows_with_market_signal, locale),
        meta: t("Rows with comparable market prices"),
        tooltip: t(HELP_TEXT.marketSignalRows),
      },
      {
        label: t("Market fallback rows"),
        value: formatRmsCount(summary.market_benchmark_fallback_rows, locale),
        meta: t("Rows where the market benchmark was ignored safely"),
        tooltip: t(HELP_TEXT.marketQuality),
      },
      {
        label: t("Stop sell rows"),
        value: formatRmsCount(summary.stop_sell_rows, locale),
        meta: t("Rows blocked by no availability or stop sell"),
        tooltip: t(HELP_TEXT.stopSellRows),
      },
      {
        label: t("Pace configured"),
        value: formatRmsCount(
          firstPresent(
            summary.pace_configured_rows,
            rowsByPaceStatus.configured
          ),
          locale
        ),
        meta: t("Rows with a complete occupancy pace snapshot"),
        tooltip: t(HELP_TEXT.paceStatus),
      },
      {
        label: t("Ahead of pace"),
        value: formatRmsCount(
          firstPresent(summary.pace_ahead_rows, rowsByPaceStatus.ahead),
          locale
        ),
        meta: t("Current occupancy is above the tolerance band"),
        tooltip: t(HELP_TEXT.paceGap),
      },
      {
        label: t("On pace"),
        value: formatRmsCount(
          firstPresent(summary.pace_on_pace_rows, rowsByPaceStatus.on_pace),
          locale
        ),
        meta: t("Current occupancy is inside the tolerance band"),
        tooltip: t(HELP_TEXT.paceStatus),
      },
      {
        label: t("Behind pace"),
        value: formatRmsCount(
          firstPresent(summary.pace_behind_rows, rowsByPaceStatus.behind),
          locale
        ),
        meta: t("Current occupancy is below the tolerance band"),
        tooltip: t(HELP_TEXT.paceGap),
      },
      {
        label: t("Pace unconfigured"),
        value: formatRmsCount(
          firstPresent(
            summary.pace_unconfigured_rows,
            rowsByPaceStatus.unconfigured
          ),
          locale
        ),
        meta: t("Legacy or non-applicable rows without a pace target"),
        tooltip: t(HELP_TEXT.paceWindow),
      },
      {
        label: t("Avg pace gap"),
        value: formatSignedPercent(summary.avg_pace_gap_pct, locale),
        meta: t("Average current occupancy minus pace target"),
        tooltip: t(HELP_TEXT.paceGap),
      },
    ];
  }, [data, locale, t]);

  const snapshotRecordGroups = useMemo(
    () => [
      {
        label: t("Signals & Forecast"),
        record: data,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Demand forecast"),
        stage: "demand_forecast",
        record: data?.runs?.demand_forecast,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Daily features"),
        stage: "daily_features",
        record: data?.runs?.daily_features,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Signal and forecast rows"),
        stage: "daily_features",
        records: data?.rows,
        requireRecordsArray: true,
        requirePipelineRunId: true,
      },
    ],
    [data, t]
  );
  const snapshotStatus = useMemo(
    () =>
      resolveRmsPipelineStatus(data?.pipeline, {
        recordGroups: snapshotRecordGroups,
      }),
    [data, snapshotRecordGroups]
  );
  const snapshotAligned = snapshotStatus.safeToRender;
  const safeToRender = snapshotAligned && !loading;

  const columns = useMemo(
    () => [
      {
        key: "room_category_name",
        label: t("Room category"),
        tooltip: t(HELP_TEXT.roomCategory),
        stickyClass: "rmo-sticky s1",
        render: (row) => row.room_category_name || "-",
      },
      {
        key: "stay_date",
        label: t("Stay date"),
        tooltip: t(HELP_TEXT.stayDate),
        stickyClass: "rmo-sticky s2",
        render: (row) => (
          <>
            <div>{safeFormatDateLabel(row.stay_date, dateLocale)}</div>
            <div className="rmo-muted">{row.stay_date || "-"}</div>
          </>
        ),
      },
      {
        key: "pace_window_code",
        label: t("Pace window"),
        tooltip: t(HELP_TEXT.paceWindow),
        render: (row) => {
          const windowCode = getPaceWindowCode(row);
          const leadDays = getPaceLeadDays(row);
          if (!windowCode) return "-";
          return (
            <>
              <div>{t(PACE_WINDOW_LABELS[windowCode] || String(windowCode))}</div>
              <div className="rmo-muted">
                {leadDays == null || leadDays === ""
                  ? "-"
                  : t("{count} lead days", { count: Number(leadDays) })}
              </div>
            </>
          );
        },
      },
      {
        key: "pace_expected_occupancy_pct",
        label: t("Pace target"),
        tooltip: t(HELP_TEXT.paceTarget),
        render: (row) => {
          const target = getPaceExpectedOccupancy(row);
          const tolerance = firstPresent(
            row.pace_tolerance_pct,
            row.tolerance_pct
          );
          return (
            <>
              <div>{safeFormatPercent(target, locale)}</div>
              <div className="rmo-muted">
                {t("Tolerance")}: {safeFormatPercent(tolerance, locale)}
              </div>
            </>
          );
        },
      },
      {
        key: "pace_current_occupancy_pct",
        label: t("Current commercial occ"),
        tooltip: t(HELP_TEXT.paceCurrent),
        render: (row) => {
          const currentOccupancy = getPaceCurrentOccupancy(row);
          const onBooksRooms = firstPresent(
            row.pace_on_books_rooms,
            row.current_on_books_rooms
          );
          const inventoryRooms = firstPresent(
            row.pace_inventory_rooms,
            row.inventory_rooms
          );
          return (
            <>
              <div>{safeFormatPercent(currentOccupancy, locale)}</div>
              <div className="rmo-muted">
                {onBooksRooms == null || inventoryRooms == null
                  ? "-"
                  : `${formatRmsCount(onBooksRooms, locale)}/${formatRmsCount(
                      inventoryRooms,
                      locale
                    )} ${t("rooms")}`}
              </div>
            </>
          );
        },
      },
      {
        key: "pace_gap_pct",
        label: t("Pace gap"),
        tooltip: t(HELP_TEXT.paceGap),
        render: (row) => formatSignedPercent(row.pace_gap_pct, locale),
      },
      {
        key: "pace_status",
        label: t("Pace status"),
        tooltip: t(HELP_TEXT.paceStatus),
        render: (row) => {
          const status = getPaceStatus(row);
          return status ? (
            <span
              className={`rmo-pill rmo-pill--${paceStatusTone(status)}`}
              title={
                row.occupancy_pace_snapshot_hash
                  ? `${t("Snapshot")} v${
                      row.occupancy_pace_snapshot_version || "-"
                    }: ${row.occupancy_pace_snapshot_hash}`
                  : undefined
              }
            >
              {t(PACE_STATUS_LABELS[status])}
            </span>
          ) : (
            <span className="rmo-pill rmo-pill--soft">
              {t("Not configured")}
            </span>
          );
        },
      },
      {
        key: "expected_final_occupancy_pct",
        label: t("Final occ"),
        tooltip: t(HELP_TEXT.finalOccupancy),
        render: (row) => safeFormatPercent(row.expected_final_occupancy_pct, locale),
      },
      {
        key: "current_on_books_rooms",
        label: t("On books"),
        tooltip: t(HELP_TEXT.onBooks),
        render: (row) => formatRmsCount(row.current_on_books_rooms, locale),
      },
      {
        key: "expected_cancel_pct",
        label: t("Cancel pct"),
        tooltip: t(HELP_TEXT.cancelPct),
        render: (row) => safeFormatPercent(row.expected_cancel_pct, locale),
      },
      {
        key: "expected_cancel_rooms",
        label: t("Cancel rooms"),
        tooltip: t(HELP_TEXT.cancelRooms),
        render: (row) => formatRmsCount(row.expected_cancel_rooms, locale),
      },
      {
        key: "expected_pickup_rooms",
        label: t("Pickup"),
        tooltip: t(HELP_TEXT.pickup),
        render: (row) => formatRmsCount(row.expected_pickup_rooms, locale),
      },
      {
        key: "expected_net_pickup_rooms",
        label: t("Net pickup"),
        tooltip: t(HELP_TEXT.netPickup),
        render: (row) => formatRmsCount(row.expected_net_pickup_rooms, locale),
      },
      {
        key: "comp_price_weighted_avg_equiv",
        label: t("Market baseline"),
        tooltip: t(HELP_TEXT.marketBaseline),
        render: (row) =>
          safeFormatCurrency(
            row.comp_price_weighted_avg_equiv,
            locale,
            row.room_rate_currency
          ),
      },
      {
        key: "comp_sources_count_equiv",
        label: t("Market sources"),
        tooltip: t(HELP_TEXT.marketSources),
        render: (row) => formatRmsCount(row.comp_sources_count_equiv, locale),
      },
      {
        key: "market_benchmark_quality",
        label: t("Market quality"),
        tooltip: t(HELP_TEXT.marketQuality),
        render: (row) => {
          const benchmark = row.market_benchmark;
          if (!benchmark || benchmark.status === "legacy") return "-";
          const used = benchmark.status === "used";
          return (
            <span
              className={`rmo-pill rmo-pill--${used ? "success" : "warn"}`}
              title={benchmark.fallback_explanation || benchmark.snapshot_hash || ""}
            >
              {used
                ? t("Accepted")
                : `${t("Ignored")} · ${benchmark.fallback_reason || t("quality fallback")}`}
            </span>
          );
        },
      },
      {
        key: "current_price",
        label: t("Current price"),
        tooltip: t(HELP_TEXT.currentPrice),
        render: (row) =>
          safeFormatCurrency(row.current_price, locale, row.room_rate_currency),
      },
      {
        key: "expected_base_price",
        label: t("Base price"),
        tooltip: t(HELP_TEXT.basePrice),
        render: (row) =>
          safeFormatCurrency(row.expected_base_price, locale, row.room_rate_currency),
      },
      {
        key: "strategy_mode",
        label: t("Strategy"),
        tooltip: t(HELP_TEXT.strategy),
        render: (row) =>
          row.strategy_mode ? (
            <span className="rmo-pill rmo-pill--soft">
              {t(STRATEGY_LABELS[row.strategy_mode] || row.strategy_mode)}
            </span>
          ) : (
            "-"
          ),
      },
      {
        key: "forecast_confidence",
        label: t("Forecast conf"),
        tooltip: t(HELP_TEXT.forecastConfidence),
        render: (row) => formatConfidence(row.forecast_confidence, locale),
      },
    ],
    [dateLocale, locale, t]
  );

  return (
    <div className="rmo-sectionStack">
      <div className="rmo-banner">
        <div className="rmo-bannerText">
          {t(
            "This table shows only objective RMS inputs: demand, pickup, cancellations, market baseline and the active strategy context for each room category and stay date."
          )}
        </div>
        <div className="rmo-inlineActions">
          <RunPill
            t={t}
            languageId={languageId}
            label={t("Forecast")}
            run={data?.runs?.demand_forecast}
            helpText={t(HELP_TEXT.forecastRun)}
          />
          <RunPill
            t={t}
            languageId={languageId}
            label={t("Daily features")}
            run={data?.runs?.daily_features}
            helpText={t(HELP_TEXT.dailyFeaturesRun)}
          />
        </div>
      </div>

      <RmsPipelineStatus
        pipeline={data?.pipeline}
        t={t}
        locale={locale}
        loading={loading}
        recordGroups={snapshotRecordGroups}
      />

      {error ? <div className="rmo-errorBox">{error}</div> : null}

      <div className="rmo-surface">
        <div className="rmo-head">
          <div>
            <h3 className="rmo-headTitle">{t("Signals & Forecast")}</h3>
            <div className="rmo-headSubtitle">
              {t("Daily demand table by room category and stay date.")}
            </div>
          </div>
        </div>

        <div className="rmo-toolbar">
          <div className="rmo-toolbarGroup">
            <select
              className="rmo-select"
              value={roomCategoryId}
              onChange={(event) => setRoomCategoryId(event.target.value)}
            >
              <option value="">{t("All room categories")}</option>
              {(meta?.room_categories || []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <input
              className="rmo-input"
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />
            <input
              className="rmo-input"
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </div>

          <div className="rmo-toolbarGroup">
            <button
              type="button"
              className="rmo-btn rmo-btn--accent"
              onClick={loadSignals}
              disabled={loading}
            >
              {loading ? t("Refreshing...") : t("Refresh")}
            </button>
          </div>
        </div>

        {safeToRender ? (
          <div className="rmo-kpiGrid">
            {summaryCards.map((card) => (
              <div key={card.label} className="rmo-kpiCard" title={card.tooltip}>
                <div className="rmo-kpiLabel">
                  <LabelWithHelp label={card.label} helpText={card.tooltip} />
                </div>
                <div className="rmo-kpiValue">{card.value}</div>
                <div className="rmo-kpiMeta">{card.meta}</div>
              </div>
            ))}
          </div>
        ) : null}

        {loading ? (
          <div className="rmo-empty">{t("Loading RMS signals...")}</div>
        ) : !snapshotAligned ? (
          <div className="rmo-empty">
            {t("Snapshot data is hidden because pipeline identity could not be verified.")}
          </div>
        ) : !data?.rows?.length ? (
          <div className="rmo-empty">{t("No signal rows found for the selected filter.")}</div>
        ) : (
          <div
            className="rmo-tableWrap"
            style={{ "--rmo-col-1": "190px", "--rmo-col-2": "128px" }}
          >
            <table className="rmo-table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key} className={column.stickyClass || ""}>
                      <LabelWithHelp label={column.label} helpText={column.tooltip} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={`${row.room_category_id}-${row.stay_date}`}>
                    {columns.map((column) => (
                      <td key={column.key} className={column.stickyClass || ""}>
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
